import React from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  Stack,
  Divider,
  useTheme,
} from "@mui/material";

function groupApplications(applications) {
  const categories = {
    "Active Job Applications": [
      "Under Review",
      "Applied",
      "Interview",
      "Assessment",
      "Offer",
      "In Progress",
      "Initial Screen",
    ],
    "Inactive Job Applications": [
      "Rejected",
      "Withdrawn",
      "Not in consideration",
      "Closed",
      "Hired",
    ],
  };

  const grouped = {};
  Object.entries(categories).forEach(([cat, statuses]) => {
    grouped[cat] = applications.filter((app) =>
      statuses.includes(app.status)
    );
  });

  return grouped;
}

function getStatusChipProps(status) {
  const normalized = (status || "").toLowerCase();

  if (["applied", "under review", "in progress"].includes(normalized)) {
    return { color: "info", label: status };
  }

  if (["interview", "assessment", "offer"].includes(normalized)) {
    return { color: "primary", label: status };
  }

  if (["hired"].includes(normalized)) {
    return { color: "success", label: status };
  }

  if (
    ["rejected", "withdrawn", "not in consideration", "closed"].includes(
      normalized
    )
  ) {
    return { color: "error", label: status };
  }

  return { color: "default", label: status || "Unknown" };
}

export default function CandidateApplicationsSection({ applications = [] }) {
  const grouped = groupApplications(applications);
  const theme = useTheme();

  return (
    <Box sx={{ width: "100%", mt: 2 }}>
      {Object.entries(grouped).map(([category, apps]) => (
        <Box key={category} mb={4}>
          {/* Category header */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1.5 }}
          >
            <Typography
              variant="overline"
              sx={{
                letterSpacing: 1.2,
                color: theme.palette.text.secondary,
              }}
            >
              {category}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.disabled }}
            >
              {apps.length} {apps.length === 1 ? "application" : "applications"}
            </Typography>
          </Stack>

          <Divider sx={{ mb: 2 }} />

          {apps.length === 0 ? (
            <Typography
              variant="body2"
              sx={{ mb: 2, color: theme.palette.text.secondary }}
            >
              No applications in this category yet.
            </Typography>
          ) : (
            <Stack spacing={2}>
              {apps.map((app, idx) => {
                const chipProps = getStatusChipProps(app.status || "");
                const jobTitle = app.joborder?.title || "Unknown role";
                const jobLocation =
                  app.joborder?.location || "Location not specified";

                return (
                  <Paper
                    key={app.id || `${category}-${idx}`}
                    sx={{
                      p: { xs: 2, sm: 2.5 },
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: "rgba(145, 158, 171, 0.16)",
                      background:
                        "linear-gradient(135deg, rgba(255,255,255,0.98), rgba(245,247,250,0.95))",
                      boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
                      transition:
                        "transform 0.15s ease, box-shadow 0.15s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 14px 32px rgba(15, 23, 42, 0.14)",
                      },
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      spacing={{ xs: 1.5, sm: 0 }}
                    >
                      {/* Left side: job info */}
                      <Box>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            mb: 0.3,
                            fontSize: { xs: 15, sm: 16 },
                          }}
                        >
                          {jobTitle}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.secondary,
                            fontSize: { xs: 12.5, sm: 13 },
                          }}
                        >
                          {jobLocation}
                        </Typography>
                      </Box>

                      {/* Right side: status and meta */}
                      <Stack
                        direction="column"
                        alignItems={{ xs: "flex-start", sm: "flex-end" }}
                        spacing={0.5}
                      >
                        <Chip
                          size="small"
                          variant="filled"
                          {...chipProps}
                          sx={{
                            textTransform: "capitalize",
                            fontWeight: 500,
                            fontSize: 12,
                            borderRadius: 999,
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ color: theme.palette.text.secondary }}
                        >
                          Applied on{" "}
                          {app.created_at
                            ? new Date(app.created_at).toLocaleDateString()
                            : "N/A"}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Box>
      ))}
    </Box>
  );
}
